/**
 * Reservations API
 *
 * All reservation-related data access functions.
 * Uses RPC functions for critical operations like reserve and withdraw.
 */

import { supabase, normalizeError } from '@/lib/supabaseClient'

/**
 * Check if buyer has previous withdrawals
 * Used to determine reservation duration (72h vs 48h)
 *
 * @returns {Promise<{data: boolean, error: Object|null}>}
 */
export async function hasPreviousWithdrawals() {
  try {
    const { data, error } = await supabase.rpc('has_previous_withdrawals', {
      p_buyer_id: (await supabase.auth.getUser()).data.user?.id,
    })

    if (error) throw error
    return { data: data || false, error: null }
  } catch (error) {
    return { data: false, error: normalizeError(error) }
  }
}

/**
 * Reserve a unit (buyer only)
 * Uses RPC function for atomic operation with proper locking
 *
 * @param {string} unitId - Unit UUID
 * @param {string} notes - Optional buyer notes
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function reserveUnit(unitId, notes = '') {
  try {
    const { data, error } = await supabase.rpc('reserve_unit', {
      p_unit_id: unitId,
      p_buyer_notes: notes || null,
    })

    if (error) throw error

    if (data) {
      await supabase.rpc('log_activity', {
        p_action_type: 'reservation_created',
        p_resource_type: 'reservation',
        p_resource_id: data.id,
        p_description: `Reserved unit ${data.unit_number || 'N/A'}`,
        p_metadata: { unit_id: unitId }
      })
    }

    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Withdraw/cancel a reservation (buyer only)
 * Uses RPC function for atomic operation
 *
 * @param {string} reservationId - Reservation UUID
 * @returns {Promise<{error: Object|null}>}
 */
export async function withdrawReservation(reservationId) {
  try {
    const { error } = await supabase.rpc('withdraw_reservation', {
      p_reservation_id: reservationId,
    })

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Fetch reservations for current buyer
 *
 * @param {string} status - Optional status filter ('active', 'withdrawn', 'expired', 'converted')
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchMyReservations(status = null) {
  try {
    const { data: rawData, error } = await supabase.rpc('get_buyer_reservations_with_discounts')

    if (error) throw error

    const reservations = (rawData || []).map(row => ({
      id: row.id,
      status: row.status,
      expires_at: row.expires_at,
      status_changed_at: row.status_changed_at,
      notes: row.notes,
      created_at: row.created_at,
      asp_initiated_at: row.asp_initiated_at,
      is_reduced_duration: row.is_reduced_duration || false,
      unit: {
        id: row.unit_id,
        unit_number: row.unit_number,
        bedrooms: row.bedrooms,
        bathrooms: row.bathrooms,
        sqm: row.sqm,
        price: row.unit_price,
        floor_plan_url: row.floor_plan_url,
        project: {
          id: row.project_id,
          title: row.project_title,
          name: row.project_title,
          location: row.project_location,
          image_urls: row.project_image_urls,
          developer: {
            id: row.developer_id,
            full_name: row.developer_name,
            company_name: row.developer_company,
            email: row.developer_email,
            phone: row.developer_phone,
          }
        }
      },
      reservation_discounts: row.discount_id ? [{
        id: row.discount_id,
        locked_dollar_amount: row.locked_dollar_amount,
        unit_price_at_lock: row.unit_price_at_lock,
        discount_allocations: row.allocation_id ? [{
          id: row.allocation_id,
          deposit_contribution: row.deposit_contribution,
          price_reduction: row.price_reduction,
          furniture_packages: row.furniture_packages,
          unallocated: row.unallocated,
        }] : []
      }] : []
    }))

    const filtered = status
      ? reservations.filter(r => r.status === status)
      : reservations

    return { data: filtered, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch single reservation by ID
 *
 * @param {string} reservationId - Reservation UUID
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function fetchReservationById(reservationId) {
  try {
    const { data, error } = await supabase
      .from('reservations')
      .select(`
        *,
        buyer:profiles!buyer_id(id, full_name, email, phone),
        unit:units(
          id,
          unit_number,
          bedrooms,
          bathrooms,
          sqm,
          price,
          floor_plan_url,
          project:projects(
            id,
            title,
            location,
            address,
            developer:profiles!developer_id(id, full_name, company_name)
          )
        )
      `)
      .eq('id', reservationId)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch reservations for developer's projects
 *
 * @param {string} projectId - Optional project filter
 * @param {string} status - Optional status filter
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchProjectReservations(projectId = null, status = null) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) throw userError
    if (!user) throw new Error('Not authenticated')

    let query = supabase
      .from('reservations')
      .select(`
        id,
        status,
        expires_at,
        status_changed_at,
        notes,
        created_at,
        buyer:profiles!buyer_id(id, full_name, email, phone),
        unit:units!inner(
          id,
          unit_number,
          bedrooms,
          bathrooms,
          sqm,
          price,
          project:projects!inner(
            id,
            title,
            developer_id
          )
        )
      `)
      .eq('unit.project.developer_id', user.id)
      .order('created_at', { ascending: false })

    if (projectId) {
      query = query.eq('unit.project.id', projectId)
    }

    if (status) {
      query = query.eq('status', status)
    }

    const { data, error } = await query

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch all active reservations (admin only)
 *
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchAllReservations() {
  try {
    const { data, error } = await supabase
      .from('reservations')
      .select(`
        *,
        buyer:profiles!buyer_id(id, full_name, email),
        unit:units(
          unit_number,
          project:projects(
            id,
            title,
            developer:profiles!developer_id(full_name, company_name)
          )
        )
      `)
      .order('created_at', { ascending: false })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Update reservation notes (buyer only, own reservations)
 *
 * @param {string} reservationId - Reservation UUID
 * @param {string} notes - Updated notes
 * @returns {Promise<{error: Object|null}>}
 */
export async function updateReservationNotes(reservationId, notes) {
  try {
    const { error } = await supabase
      .from('reservations')
      .update({ notes })
      .eq('id', reservationId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Check if a unit is available for reservation
 *
 * @param {string} unitId - Unit UUID
 * @returns {Promise<{data: boolean, error: Object|null}>}
 */
export async function isUnitAvailable(unitId) {
  try {
    const { data, error } = await supabase
      .from('reservations')
      .select('id')
      .eq('unit_id', unitId)
      .eq('status', 'active')
      .gte('expires_at', new Date().toISOString())
      .maybeSingle()

    if (error) throw error
    return { data: !data, error: null } // Available if no active reservation found
  } catch (error) {
    return { data: false, error: normalizeError(error) }
  }
}

/**
 * Get reservation statistics for a project (developer only)
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function getProjectReservationStats(projectId) {
  try {
    const { data, error } = await supabase
      .from('reservations')
      .select('status, unit:units!inner(project_id)')
      .eq('unit.project_id', projectId)

    if (error) throw error

    const stats = {
      total: data.length,
      active: data.filter(r => r.status === 'active').length,
      withdrawn: data.filter(r => r.status === 'withdrawn').length,
      expired: data.filter(r => r.status === 'expired').length,
      converted: data.filter(r => r.status === 'converted').length,
    }

    return { data: stats, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}
